import "./modal.css";

import { CloseOutlined } from "@ant-design/icons";
import { useUpdateEffect } from "ahooks";
import { Button, DatePicker, Form, Input, Modal, Space, Tooltip } from "antd";
import dayjs from "dayjs";
import { isNil } from "ramda";
import { FC } from "react";
import { v4 as uuidv4 } from "uuid";

import { findNodeByKeys } from "@/libs/structures";
import { PreviewTutorialModal } from "@/pages/department-course/component/preview-tutorial/preview-tutorial";
import { getMimeType } from "@/utils/file";
import { localGet } from "@/utils/storage";

import { Icon } from "../icon";
import { CoursewareUpload } from "./courseware-upload";
import styles from "./create-tutorial.module.less";
import { PersonnelScopeInput } from "./personnel-scope-input";
import { IGenerateTutorialProps } from "./types";
import { useStore } from "./use-store";

export const CreateTutorial: FC<IGenerateTutorialProps> = (props) => {
  const { open, defaultData } = props;

  const { TextArea } = Input;

  const { RangePicker } = DatePicker;

  const {
    t,
    contextHolder,
    form,
    loading,
    previewInfo,
    previewIndex,
    openPreviewModal,
    openPreviewModalAction,
    formIsChanged,
    isShowPodcast,
    handleOk,
    handleCancel,
    handlePreview,
    handleIntelligentCreation,
  } = useStore(props);

  useUpdateEffect(() => {
    const isIntelligentCreation = JSON.parse(localGet("isIntelligentCreation"));

    if (defaultData && isNil(isIntelligentCreation)) {
      const { title, description, personnelScope, shelfTime, courseware } =
        defaultData;

      form.setFieldsValue({
        title,
        description,
        personnelScope: findNodeByKeys(personnelScope),
        shelfTime: shelfTime.map((item) => dayjs(item)),
        courseware: courseware.map((item) => item),
      });
    }
    if (isNil(isIntelligentCreation)) {
      return;
    }
    formIsChanged.current = true;
    const mediaInfo = JSON.parse(sessionStorage.getItem("mediaInfo") || "{}");

    if (!isNil(mediaInfo) && !isNil(mediaInfo.fileUrl)) {
      const parts = mediaInfo.fileUrl.split(/[\\/]/); // 同时匹配正斜杠(/)和反斜杠(\)

      const fileNameWithExtension = parts[parts.length - 1].split("?")[0];

      const fileExtension = fileNameWithExtension
        .split(".")
        .pop()
        ?.toLowerCase();

      const mimeType = getMimeType(fileExtension || "");

      form.setFieldsValue({
        ...isIntelligentCreation,
        personnelScope: isNil(isIntelligentCreation.personnelScope)
          ? null
          : findNodeByKeys(isIntelligentCreation.personnelScope),
        shelfTime: isNil(isIntelligentCreation.shelfTime)
          ? null
          : isIntelligentCreation.shelfTime.map((item: string) => dayjs(item)),
        courseware: [
          {
            uid: uuidv4(),
            url: mediaInfo.fileUrl,
            type: mimeType,
            path: mediaInfo.relativePath,
            name: fileNameWithExtension,
            podcastId: mediaInfo.podcastId,
          },
        ],
      });

      return;
    }

    form.setFieldsValue({
      ...isIntelligentCreation,
      personnelScope: isNil(isIntelligentCreation.personnelScope)
        ? null
        : findNodeByKeys(isIntelligentCreation.personnelScope),
      shelfTime: isNil(isIntelligentCreation.shelfTime)
        ? null
        : isIntelligentCreation.shelfTime.map((item: string) => dayjs(item)),
    });
  }, [defaultData, open]);

  return (
    <>
      <Modal
        width={786}
        open={open}
        maskClosable={false}
        keyboard={false}
        footer={null}
        className="quick-generate-tutorial-modal"
        closable={false}
      >
        <div className={styles["modal-header"]}>
          <span>{t("intelligent_creation.intelligent_creation")}</span>
          <CloseOutlined onClick={handleCancel} />
        </div>
        <Form
          form={form}
          className={styles["modal-body"]}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          autoComplete="off"
          onChange={() => {
            formIsChanged.current = true;
          }}
        >
          <Form.Item
            label={t("name")}
            name="title"
            rules={[{ required: true }]}
          >
            <Input autoComplete="off" style={{ width: 400 }} />
          </Form.Item>
          <Form.Item
            label={t("description")}
            name="description"
            rules={[{ required: true }]}
          >
            <TextArea rows={4} style={{ width: 400 }} />
          </Form.Item>
          <Form.Item
            label={t("personnel_scope")}
            name="personnelScope"
            rules={[{ required: true, type: "array" }]}
          >
            <PersonnelScopeInput />
          </Form.Item>
          <Form.Item
            label={t("shelf_time")}
            name="shelfTime"
            rules={[{ required: true }]}
          >
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: 400 }}
            />
          </Form.Item>
          <Form.Item
            label={t("tutorials")}
            name="courseware"
            rules={[{ required: true, type: "array" }]}
          >
            <CoursewareUpload onPreview={handlePreview} />
          </Form.Item>
        </Form>
        <div className={styles["modal-footer"]}>
          <Space>
            {isShowPodcast ? (
              <Button
                className={styles["intelligent-creation"]}
                onClick={handleIntelligentCreation}
              >
                <Icon
                  icon="intelligentCreation"
                  style={{ marginRight: "6px" }}
                />
                {t("intelligent_creation.intelligent_creation")}
              </Button>
            ) : (
              <Tooltip placement="top" title={t("no_permissions")}>
                <Button
                  className={styles["intelligent-creation"]}
                  onClick={handleIntelligentCreation}
                >
                  <Icon
                    icon="intelligentCreation"
                    style={{ marginRight: "6px" }}
                  />
                  {t("intelligent_creation.intelligent_creation")}
                </Button>
              </Tooltip>
            )}
            <Button className={styles["cancel-btn"]} onClick={handleCancel}>
              {t("cancel")}
            </Button>
            <Button
              className={styles["ok-btn"]}
              onClick={handleOk}
              loading={loading}
            >
              {t("confirm")}
            </Button>
          </Space>
        </div>
      </Modal>

      <PreviewTutorialModal
        open={openPreviewModal}
        index={previewIndex}
        data={previewInfo}
        onClose={() => {
          openPreviewModalAction.setFalse();
        }}
      />

      {contextHolder}
    </>
  );
};
